import { useEffect, useState } from "react";
import API_BASE from "../../../../config";
import { authHeaders } from "../sectionUtils";
import s from "./SectionBooksTab.module.css";

export default function SectionBooksTab({ section }) {
  const [requiredBooks, setRequiredBooks] = useState([]);
  const [books,         setBooks]         = useState({});
  const [subjects,      setSubjects]      = useState({});
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState("");

  useEffect(() => {
    if (!section?.id || !section?.schoolYearId) return;
    let cancelled = false;
    setLoading(true); setError("");

    /* First fetch the class offerings to get the subjectIds for this section */
    fetch(`${API_BASE}/api/class-offerings?sectionId=${section.id}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(offerings => {
        const subjectIds = [...new Set(
          (Array.isArray(offerings) ? offerings : []).map(o => o.subjectId).filter(Boolean)
        )];

        return Promise.all([
          subjectIds.length > 0
            ? fetch(
                `${API_BASE}/api/required-books?schoolYearId=${section.schoolYearId}&subjectIds=${subjectIds.join(",")}`,
                { headers: authHeaders() }
              ).then(r => r.ok ? r.json() : []).catch(() => [])
            : Promise.resolve([]),
          fetch(`${API_BASE}/api/books`, { headers: authHeaders() })
            .then(r => r.ok ? r.json() : []).catch(() => []),
          subjectIds.length > 0
            ? fetch(`${API_BASE}/api/subjects`, { headers: authHeaders() })
                .then(r => r.ok ? r.json() : []).catch(() => [])
            : Promise.resolve([]),
        ]);
      })
      .then(([rbData, bookData, subData]) => {
        if (cancelled) return;
        setRequiredBooks(Array.isArray(rbData) ? rbData : []);
        setBooks(Object.fromEntries((Array.isArray(bookData) ? bookData : []).map(b => [b.id, b])));
        setSubjects(Object.fromEntries((Array.isArray(subData) ? subData : []).map(sub => [sub.id, sub])));
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) { setError("Could not load required books."); setLoading(false); }
      });

    return () => { cancelled = true; };
  }, [section?.id, section?.schoolYearId]);

  if (loading) return <div className={s.loading}>Loading required books…</div>;
  if (error)   return <div className={s.errorBanner}>{error}</div>;
  if (requiredBooks.length === 0) return (
    <div className={s.empty}>
      <div className={s.emptyIcon}>📖</div>
      <div className={s.emptyText}>No required books assigned to this section's subjects.</div>
    </div>
  );

  /* Group by subject */
  const bySubject = new Map();
  requiredBooks.forEach(rb => {
    const key = rb.subjectId ?? "none";
    if (!bySubject.has(key)) bySubject.set(key, []);
    bySubject.get(key).push(rb);
  });

  const mandatory    = requiredBooks.filter(rb => rb.mandatory);
  const supplemental = requiredBooks.filter(rb => !rb.mandatory);

  return (
    <div className={s.root}>
      <div className={s.summary}>
        <span className={s.summaryItem}>
          <strong>{requiredBooks.length}</strong> book{requiredBooks.length !== 1 ? "s" : ""} total
        </span>
        {mandatory.length > 0 && (
          <span className={s.summaryItem}>
            <strong>{mandatory.length}</strong> mandatory
          </span>
        )}
        {supplemental.length > 0 && (
          <span className={s.summaryItem}>
            <strong>{supplemental.length}</strong> supplemental
          </span>
        )}
      </div>

      {[...bySubject.entries()].map(([subjectId, rbs]) => {
        const subject = subjects[subjectId];
        const subjectName = subject?.title || subject?.name || `Subject #${subjectId}`;
        return (
          <div key={subjectId} className={s.subjectGroup}>
            <div className={s.subjectHead}>{subjectName}</div>
            <div className={s.bookList}>
              {rbs.map(rb => {
                const book = books[rb.bookId];
                return (
                  <div key={rb.id} className={s.bookCard}>
                    <span className={s.bookIcon} aria-hidden="true">
                      {rb.mandatory ? "📗" : "📘"}
                    </span>
                    <div className={s.bookInfo}>
                      <div className={s.bookTitle}>{book?.title || `Book #${rb.bookId}`}</div>
                      {book?.author && <div className={s.bookMeta}>{book.author}</div>}
                      {book?.isbn   && <div className={s.bookMeta}>ISBN: {book.isbn}</div>}
                    </div>
                    <span className={`${s.mandatoryBadge} ${rb.mandatory ? s.mandatory : s.supplemental}`}>
                      {rb.mandatory ? "Mandatory" : "Supplemental"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
